import * as React from "react";
import { useConvex } from "convex/react";
import { api } from "@/lib/convexApi";
import type { DateRange } from "react-day-picker";
import { CalendarDays, Download, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  buildQuickbooksCsv,
  downloadCsv,
  quickbooksExportFilename,
  type QuickbooksInvoice,
} from "@/lib/quickbooks-export";

interface ExportWarning {
  invoiceNo: string;
  orderId: string;
  field: string;
  message: string;
}

interface FeeSyncResult {
  ordersScanned: number;
  feeRecordedCharges: number;
  backfilled: { orderNumber: string; fee: number }[];
  failed: { orderNumber: string; reason: string }[];
  unsettled: FeeSyncOrderEntry[];
  outsideStripe: FeeSyncOrderEntry[];
  note?: string;
}

interface FeeSyncOrderEntry {
  orderNumber: string;
  total: number | null;
  method: string | null;
  paymentStatus: string | null;
}

interface FeeCheckResult {
  ordersScanned: number;
  feeRecordedCharges: number;
  missingCount: number;
  missing: string[];
  unsettled: FeeSyncOrderEntry[];
  outsideStripe: FeeSyncOrderEntry[];
}

type ExportDeltaStatus = "new" | "changed" | "unchanged";

interface ExportLastBatch {
  createdAt: number;
  invoiceCount: number;
  exportedByLabel: string;
}

interface ExportInvoiceRow extends QuickbooksInvoice {
  fingerprint: string;
  deltaStatus?: ExportDeltaStatus;
}

interface ExportQueryResult {
  invoices: ExportInvoiceRow[];
  warnings: ExportWarning[];
  lastBatch: ExportLastBatch | null;
}

/** Fingerprints of the CSV actually downloaded — recorded only when the user
 * confirms the import, so an abandoned download never moves the baseline. */
interface ImportSnapshot {
  fingerprints: { invoiceNo: string; hash: string }[];
  warningCount: number;
}

interface DeltaCounts {
  new: number;
  changed: number;
  unchanged: number;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  card: "card",
  ach: "ACH",
  credit: "net terms",
  cheque: "check/wire",
  paypal: "PayPal",
};

function feeEntryDetail(entry: FeeSyncOrderEntry): string {
  const parts: string[] = [];
  if (entry.method) {
    parts.push(PAYMENT_METHOD_LABELS[entry.method] ?? entry.method);
  }
  if (entry.paymentStatus) parts.push(entry.paymentStatus.replace(/_/g, " "));
  return parts.length > 0 ? ` — ${parts.join(", ")}` : "";
}

function feeCheckStatusLine(check: FeeCheckResult): string {
  const parts: string[] = [];
  if (check.missingCount > 0) {
    parts.push(
      `${check.missingCount} charge${check.missingCount === 1 ? "" : "s"} awaiting exact fees`,
    );
  } else {
    parts.push("All available Stripe fees are recorded");
  }
  if (check.unsettled.length > 0) {
    parts.push(`${check.unsettled.length} awaiting payment`);
  }
  if (check.outsideStripe.length > 0) {
    parts.push(`${check.outsideStripe.length} paid outside Stripe`);
  }
  return parts.join(" · ");
}

function countDeltas(invoices: ExportInvoiceRow[]): DeltaCounts {
  const counts: DeltaCounts = { new: 0, changed: 0, unchanged: 0 };
  for (const invoice of invoices) {
    if (invoice.deltaStatus === "new") counts.new += 1;
    else if (invoice.deltaStatus === "changed") counts.changed += 1;
    else if (invoice.deltaStatus === "unchanged") counts.unchanged += 1;
  }
  return counts;
}

function formatBatchTimestamp(epochMs: number): string {
  return new Date(epochMs).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const CUSTOM_RANGE = "custom";

function buildMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en-US", { month: "long", year: "numeric" });
    options.push({ value, label });
  }
  return options;
}

function getMonthRange(monthValue: string): { start: Date; end: Date } {
  const parts = monthValue.split("-").map(Number);
  const year = parts[0]!;
  const month = parts[1]!;
  return {
    start: new Date(year, month - 1, 1),
    end: new Date(year, month, 0, 23, 59, 59, 999),
  };
}

function formatRangeLabel(range: DateRange | undefined): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  if (!range?.from) return "Pick a date range";
  if (!range.to) return `${fmt(range.from)} – Pick end`;
  return `${fmt(range.from)} – ${fmt(range.to)}`;
}

export function QuickBooksExportDialog({
  authUserId,
}: {
  authUserId: string | undefined;
}) {
  const convex = useConvex();
  const monthOptions = React.useMemo(buildMonthOptions, []);
  const [open, setOpen] = React.useState(false);
  const [monthValue, setMonthValue] = React.useState(monthOptions[0]!.value);
  const [customRange, setCustomRange] = React.useState<DateRange | undefined>();
  const [isExporting, setIsExporting] = React.useState(false);
  const [warnings, setWarnings] = React.useState<ExportWarning[]>([]);
  const [isSyncingFees, setIsSyncingFees] = React.useState(false);
  const [feeSync, setFeeSync] = React.useState<FeeSyncResult | null>(null);
  const [feeCheck, setFeeCheck] = React.useState<FeeCheckResult | null>(null);
  const [feeCheckFailed, setFeeCheckFailed] = React.useState(false);
  const [isCheckingFees, setIsCheckingFees] = React.useState(false);
  const [previewLoaded, setPreviewLoaded] = React.useState(false);
  const [previewFailed, setPreviewFailed] = React.useState(false);
  const [lastBatch, setLastBatch] = React.useState<ExportLastBatch | null>(null);
  const [deltaCounts, setDeltaCounts] = React.useState<DeltaCounts | null>(null);
  const [onlyChanged, setOnlyChanged] = React.useState(false);
  const [pendingSnapshot, setPendingSnapshot] =
    React.useState<ImportSnapshot | null>(null);
  const [isMarkingImported, setIsMarkingImported] = React.useState(false);
  const previewRequestRef = React.useRef(0);

  const isCustom = monthValue === CUSTOM_RANGE;

  const resolvedRange = React.useMemo((): { start: Date; end: Date } | null => {
    if (!isCustom) return getMonthRange(monthValue);
    if (!customRange?.from || !customRange?.to) return null;
    const start = new Date(
      customRange.from.getFullYear(),
      customRange.from.getMonth(),
      customRange.from.getDate(),
    );
    const end = new Date(
      customRange.to.getFullYear(),
      customRange.to.getMonth(),
      customRange.to.getDate(),
      23,
      59,
      59,
      999,
    );
    return { start, end };
  }, [isCustom, monthValue, customRange]);

  const refreshPreview = React.useCallback(async () => {
    if (!authUserId || !resolvedRange) return;
    const requestId = ++previewRequestRef.current;
    const rangeArgs = {
      authUserId,
      startDate: resolvedRange.start.getTime(),
      endDate: resolvedRange.end.getTime(),
    };
    setIsCheckingFees(true);
    const checkPromise = (async () => {
      try {
        const result = (await convex.query(
          api.stripePayoutsSync.checkStripeFeesForRange,
          rangeArgs,
        )) as FeeCheckResult;
        return { ok: true as const, result };
      } catch (error) {
        console.error("[QuickBooksExportDialog] Fee check failed:", error);
        return { ok: false as const };
      }
    })();
    const exportPromise = (async () => {
      try {
        const result = (await convex.query(
          api.accountingQuickbooksExport.getQuickbooksInvoiceExport,
          isCustom ? rangeArgs : { ...rangeArgs, monthKey: monthValue },
        )) as ExportQueryResult;
        return { ok: true as const, result };
      } catch (error) {
        console.error("[QuickBooksExportDialog] Export preview failed:", error);
        return { ok: false as const };
      }
    })();
    const [check, preview] = await Promise.all([checkPromise, exportPromise]);
    if (requestId !== previewRequestRef.current) return;
    setIsCheckingFees(false);
    if (check.ok) {
      setFeeCheck(check.result);
      setFeeCheckFailed(false);
    } else {
      setFeeCheck(null);
      setFeeCheckFailed(true);
    }
    if (preview.ok) {
      setLastBatch(preview.result.lastBatch);
      setDeltaCounts(
        preview.result.lastBatch ? countDeltas(preview.result.invoices) : null,
      );
      setPreviewFailed(false);
    } else {
      setLastBatch(null);
      setDeltaCounts(null);
      setPreviewFailed(true);
    }
    setPreviewLoaded(true);
  }, [authUserId, convex, isCustom, monthValue, resolvedRange]);

  React.useEffect(() => {
    if (!open || !authUserId || !resolvedRange) return;
    setPreviewLoaded(false);
    setFeeCheck(null);
    setFeeCheckFailed(false);
    // A range change invalidates any not-yet-confirmed download snapshot.
    setPendingSnapshot(null);
    const timer = setTimeout(() => {
      void refreshPreview();
    }, 350);
    return () => clearTimeout(timer);
  }, [open, authUserId, resolvedRange, refreshPreview]);

  const handleExport = React.useCallback(async () => {
    if (!authUserId || !resolvedRange) return;
    setIsExporting(true);
    setWarnings([]);
    const rangeArgs = {
      authUserId,
      startDate: resolvedRange.start.getTime(),
      endDate: resolvedRange.end.getTime(),
    };
    try {
      // 1. Re-check fees; if any settled charges are missing fees, sync first.
      let check: FeeCheckResult | null = null;
      try {
        check = (await convex.query(
          api.stripePayoutsSync.checkStripeFeesForRange,
          rangeArgs,
        )) as FeeCheckResult;
        setFeeCheck(check);
        setFeeCheckFailed(false);
      } catch (error) {
        console.error("[QuickBooksExportDialog] Fee check failed:", error);
        setFeeCheckFailed(true);
      }
      if (check && check.missingCount > 0) {
        setIsSyncingFees(true);
        try {
          const syncResult = (await convex.action(
            api.stripePayoutsSync.syncStripeFeesForRange,
            rangeArgs,
          )) as FeeSyncResult;
          setFeeSync(syncResult);
          try {
            const recheck = (await convex.query(
              api.stripePayoutsSync.checkStripeFeesForRange,
              rangeArgs,
            )) as FeeCheckResult;
            setFeeCheck(recheck);
          } catch (error) {
            console.error(
              "[QuickBooksExportDialog] Fee re-check failed:",
              error,
            );
          }
        } catch (error: any) {
          console.error("[QuickBooksExportDialog] Fee sync failed:", error);
          toast.warning(
            "Stripe fee sync failed — review the fee warnings before importing",
            { description: error?.message },
          );
        } finally {
          setIsSyncingFees(false);
        }
      }

      // 2. Fetch the export and build the CSV.
      const result = (await convex.query(
        api.accountingQuickbooksExport.getQuickbooksInvoiceExport,
        isCustom ? rangeArgs : { ...rangeArgs, monthKey: monthValue },
      )) as ExportQueryResult;
      setWarnings(result.warnings);
      setLastBatch(result.lastBatch);
      setDeltaCounts(
        result.lastBatch ? countDeltas(result.invoices) : null,
      );
      setPreviewLoaded(true);
      setPreviewFailed(false);
      if (result.invoices.length === 0) {
        toast.error("No invoices found in the selected range");
        return;
      }
      let invoicesToExport: ExportInvoiceRow[] = result.invoices;
      if (onlyChanged && result.lastBatch) {
        invoicesToExport = result.invoices.filter(
          (invoice) => invoice.deltaStatus !== "unchanged",
        );
        if (invoicesToExport.length === 0) {
          toast.info(
            "All invoices are unchanged since the last export — nothing to download",
          );
          return;
        }
      }
      const csv = buildQuickbooksCsv(invoicesToExport as QuickbooksInvoice[]);
      downloadCsv(
        csv,
        quickbooksExportFilename(resolvedRange.start, resolvedRange.end),
      );
      toast.success(
        `Exported ${invoicesToExport.length} invoice${invoicesToExport.length === 1 ? "" : "s"} — after importing into QuickBooks, mark it imported below`,
      );
      if (result.warnings.length > 0) {
        toast.warning(
          `${result.warnings.length} warning${result.warnings.length === 1 ? "" : "s"} — review before importing`,
        );
      }

      // 3. Hold the downloaded snapshot; the baseline only moves when the
      // user confirms the QuickBooks import (an abandoned download must not
      // make future "only new & changed" exports skip these invoices).
      // Full-range fingerprints even for a filtered CSV: unchanged invoices
      // already match the previous baseline, so after importing the filtered
      // file QuickBooks holds exactly this state.
      setPendingSnapshot({
        fingerprints: result.invoices.map((invoice) => ({
          invoiceNo: invoice.invoiceNo,
          hash: invoice.fingerprint,
        })),
        warningCount: result.warnings.length,
      });
    } catch (error: any) {
      console.error("[QuickBooksExportDialog] Export failed:", error);
      toast.error(error?.message ?? "Export failed");
    } finally {
      setIsExporting(false);
    }
  }, [authUserId, convex, isCustom, monthValue, onlyChanged, resolvedRange]);

  const handleMarkImported = React.useCallback(async () => {
    if (!authUserId || !resolvedRange || !pendingSnapshot) return;
    setIsMarkingImported(true);
    try {
      await convex.mutation(
        api.accountingQuickbooksExport.recordQuickbooksExportBatch,
        {
          authUserId,
          startDate: resolvedRange.start.getTime(),
          endDate: resolvedRange.end.getTime(),
          ...(isCustom ? {} : { monthKey: monthValue }),
          snapshot: pendingSnapshot,
        },
      );
      setPendingSnapshot(null);
      toast.success("Export marked as imported — baseline updated");
      await refreshPreview();
    } catch (error: any) {
      console.error("[QuickBooksExportDialog] Mark imported failed:", error);
      toast.error(error?.message ?? "Could not record the import");
    } finally {
      setIsMarkingImported(false);
    }
  }, [
    authUserId,
    convex,
    isCustom,
    monthValue,
    pendingSnapshot,
    refreshPreview,
    resolvedRange,
  ]);

  const handleSyncFees = React.useCallback(async () => {
    if (!authUserId || !resolvedRange) return;
    setIsSyncingFees(true);
    setFeeSync(null);
    const rangeArgs = {
      authUserId,
      startDate: resolvedRange.start.getTime(),
      endDate: resolvedRange.end.getTime(),
    };
    try {
      const result = (await convex.action(
        api.stripePayoutsSync.syncStripeFeesForRange,
        rangeArgs,
      )) as FeeSyncResult;
      setFeeSync(result);
      if (result.backfilled.length > 0) {
        toast.success(
          `Filled ${result.backfilled.length} Stripe fee${result.backfilled.length === 1 ? "" : "s"} — download the CSV again to include them`,
        );
      } else if (result.failed.length === 0) {
        toast.success("No missing Stripe fees in this range");
      }
      try {
        const recheck = (await convex.query(
          api.stripePayoutsSync.checkStripeFeesForRange,
          rangeArgs,
        )) as FeeCheckResult;
        setFeeCheck(recheck);
        setFeeCheckFailed(false);
      } catch (error) {
        console.error("[QuickBooksExportDialog] Fee re-check failed:", error);
      }
    } catch (error: any) {
      console.error("[QuickBooksExportDialog] Fee sync failed:", error);
      toast.error(error?.message ?? "Stripe fee sync failed");
    } finally {
      setIsSyncingFees(false);
    }
  }, [authUserId, convex, resolvedRange]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setWarnings([]);
          setFeeSync(null);
          setFeeCheck(null);
          setFeeCheckFailed(false);
          setIsCheckingFees(false);
          setPreviewLoaded(false);
          setPreviewFailed(false);
          setLastBatch(null);
          setDeltaCounts(null);
          setOnlyChanged(false);
          setPendingSnapshot(null);
          setIsMarkingImported(false);
          previewRequestRef.current++;
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" disabled={!authUserId}>
          <Download className="mr-2 h-4 w-4" />
          Export for QuickBooks
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export for QuickBooks</DialogTitle>
          <DialogDescription>
            Download invoices as a QuickBooks Online import CSV. Cancelled and
            abandoned orders are excluded.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Select value={monthValue} onValueChange={setMonthValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
              <SelectItem value={CUSTOM_RANGE}>Custom range…</SelectItem>
            </SelectContent>
          </Select>

          {isCustom && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {formatRangeLabel(customRange)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={customRange}
                  onSelect={setCustomRange}
                  numberOfMonths={2}
                  defaultMonth={customRange?.from}
                />
              </PopoverContent>
            </Popover>
          )}

          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Stripe processing fees</p>
                <p className="text-xs text-muted-foreground">
                  Missing fees are checked automatically and synced from Stripe
                  before download. If an ACH fee is not available yet, the CSV
                  uses a labeled 0.8% estimate capped at $5.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncFees}
                disabled={isSyncingFees || !authUserId || !resolvedRange}
              >
                {isSyncingFees ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                )}
                {isSyncingFees ? "Syncing…" : "Sync fees"}
              </Button>
            </div>

            {isCheckingFees ? (
              <p className="text-xs text-muted-foreground">
                Checking Stripe fees…
              </p>
            ) : feeCheckFailed ? (
              <p className="text-xs text-muted-foreground">
                Fee check unavailable — you can still sync manually.
              </p>
            ) : feeCheck ? (
              <p
                className={`text-xs ${feeCheck.missingCount > 0 ? "text-foreground" : "text-muted-foreground"}`}
              >
                {feeCheckStatusLine(feeCheck)}
              </p>
            ) : null}

            {feeSync && (
              <div className="max-h-56 space-y-2 overflow-y-auto text-xs">
                {feeSync.note && (
                  <p className="text-muted-foreground">{feeSync.note}</p>
                )}
                <p className="text-muted-foreground">
                  Scanned {feeSync.ordersScanned} invoice
                  {feeSync.ordersScanned === 1 ? "" : "s"};{" "}
                  {feeSync.feeRecordedCharges} charge
                  {feeSync.feeRecordedCharges === 1 ? "" : "s"} already had a
                  fee.
                </p>
                {feeSync.backfilled.length > 0 && (
                  <div>
                    <p className="font-medium">
                      Filled {feeSync.backfilled.length} missing fee
                      {feeSync.backfilled.length === 1 ? "" : "s"} ($
                      {feeSync.backfilled
                        .reduce((sum, entry) => sum + entry.fee, 0)
                        .toFixed(2)}{" "}
                      total):
                    </p>
                    <ul className="list-disc pl-4">
                      {feeSync.backfilled.map((entry, index) => (
                        <li key={`${entry.orderNumber}-${index}`}>
                          {entry.orderNumber} — ${entry.fee.toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {feeSync.failed.length > 0 && (
                  <div className="text-destructive">
                    <p className="font-medium">
                      Exact fees not available yet ({feeSync.failed.length}):
                    </p>
                    <ul className="list-disc pl-4">
                      {feeSync.failed.map((entry, index) => (
                        <li key={`${entry.orderNumber}-${index}`}>
                          {entry.orderNumber} — {entry.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {feeSync.unsettled.length > 0 && (
                  <div className="text-muted-foreground">
                    <p className="font-medium">
                      Awaiting payment — no Stripe fee yet (
                      {feeSync.unsettled.length}):
                    </p>
                    <ul className="list-disc pl-4">
                      {feeSync.unsettled.map((entry) => (
                        <li key={entry.orderNumber}>
                          {entry.orderNumber}
                          {feeEntryDetail(entry)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {feeSync.outsideStripe.length > 0 && (
                  <div className="text-muted-foreground">
                    <p className="font-medium">
                      Paid outside Stripe — no fee applies (
                      {feeSync.outsideStripe.length}):
                    </p>
                    <ul className="list-disc pl-4">
                      {feeSync.outsideStripe.map((entry) => (
                        <li key={entry.orderNumber}>
                          {entry.orderNumber}
                          {feeEntryDetail(entry)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {warnings.length > 0 && (
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200">
              <p className="font-medium">
                {warnings.length} warning{warnings.length === 1 ? "" : "s"} —
                the CSV was still generated; review these rows before
                importing:
              </p>
              <ul className="list-disc space-y-0.5 pl-4">
                {warnings.map((warning, index) => (
                  <li key={`${warning.orderId}-${warning.field}-${index}`}>
                    {warning.invoiceNo ? `${warning.invoiceNo}: ` : ""}
                    {warning.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-1.5 rounded-md border p-3 text-xs">
            {!previewLoaded ? (
              <p className="text-muted-foreground">Checking export history…</p>
            ) : previewFailed ? (
              <p className="text-muted-foreground">
                Export history unavailable for this range.
              </p>
            ) : lastBatch ? (
              <>
                <p className="text-muted-foreground">
                  Last imported {formatBatchTimestamp(lastBatch.createdAt)} by{" "}
                  {lastBatch.exportedByLabel} — {lastBatch.invoiceCount}{" "}
                  invoice{lastBatch.invoiceCount === 1 ? "" : "s"}
                </p>
                {deltaCounts && (
                  <p>
                    {deltaCounts.new} new · {deltaCounts.changed} changed ·{" "}
                    {deltaCounts.unchanged} unchanged since last import
                  </p>
                )}
                <label className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={onlyChanged}
                    onCheckedChange={(value) => setOnlyChanged(value === true)}
                  />
                  <span>Only new &amp; changed invoices</span>
                </label>
              </>
            ) : (
              <p className="text-muted-foreground">
                Never imported into QuickBooks for this range
              </p>
            )}
            {pendingSnapshot && (
              <div className="flex items-center justify-between gap-2 border-t pt-1.5">
                <span className="text-muted-foreground">
                  Imported this CSV into QuickBooks?
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkImported}
                  disabled={isMarkingImported}
                >
                  {isMarkingImported ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  Mark as imported
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={handleExport}
            disabled={isExporting || !authUserId || !resolvedRange}
          >
            {isExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {isExporting ? "Exporting…" : "Download CSV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
